import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { TCalendarView } from '../shared';

export interface IViewSwitcherProps {
  /** The currently active cockpit view. */
  value: TCalendarView;
  /** Called with the next view when the user switches. */
  onChange: (next: TCalendarView) => void;
  /** Page the anchor backward (-1) or forward (+1) by one week/month. */
  onNavigate: (dir: -1 | 1) => void;
  /** Reset the anchor to today (and the scope to the view's natural default). */
  onToday: () => void;
  /** Human-readable label for the active period (from `periodLabel`). */
  periodLabel: string;
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;

  /* AntD gives the checked/hovered segmented button \`z-index: 1\` (to overlap a
     neighbour's border), which makes it establish a stacking context and poke
     THROUGH nav-menu overlays that don't set their own z-index. Keep these in
     normal flow so overlays always paint above them. */
  .ant-radio-button-wrapper,
  .ant-radio-button-wrapper:hover,
  .ant-radio-button-wrapper-checked {
    z-index: auto;
  }
`;

const NavGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Period = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin-left: auto;
  min-width: 0;

  .label {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    white-space: nowrap;
  }

  .value {
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const VIEW_ORDER: TCalendarView[] = [
  'today',
  'thisWeek',
  'nextWeek',
  'sixWeeks',
  'month',
];

/**
 * Segmented Today / This week / Next week / 6 weeks / Month switcher plus the
 * ←/Today/→ paging controls and the active-period label. Stays on a single row
 * and wraps gracefully when the container is narrow.
 */
export const ViewSwitcher = memo<IViewSwitcherProps>(
  ({ value, onChange, onNavigate, onToday, periodLabel }) => {
    const { t } = useTranslation();

    return (
      <Bar>
        <Radio.Group
          value={value}
          onChange={(e) => onChange(e.target.value as TCalendarView)}
          optionType="button"
          buttonStyle="solid"
        >
          {VIEW_ORDER.map((view) => (
            <Radio.Button key={view} value={view}>
              {t(`pages.paymentCalendar.views.${view}`)}
            </Radio.Button>
          ))}
        </Radio.Group>

        <NavGroup>
          <Button
            icon={<LeftOutlined />}
            onClick={() => onNavigate(-1)}
            title={t('pages.paymentCalendar.nav.prev')}
            aria-label={t('pages.paymentCalendar.nav.prev')}
          />
          <Button onClick={onToday}>
            {t('pages.paymentCalendar.nav.today')}
          </Button>
          <Button
            icon={<RightOutlined />}
            onClick={() => onNavigate(1)}
            title={t('pages.paymentCalendar.nav.next')}
            aria-label={t('pages.paymentCalendar.nav.next')}
          />
        </NavGroup>

        {periodLabel ? (
          <Period>
            <span className="value" title={periodLabel}>
              {periodLabel}
            </span>
          </Period>
        ) : null}
      </Bar>
    );
  },
);
